/**
 * Add Billing Item dialog — EB-3642 Flow B.
 *
 * Opened from the "Add Billing" button in CustomerBillingComponent.
 * Lets a back-office user create a single billing line item for the current
 * customer (POST /invoice/v1/billings).
 *
 * State machine (see spec EB-3642-add-billing-item.md §Architectural Flows):
 *   IDLE -> LOADING (fetch groups, then per-feature default prices)
 *        -> READY  (default UA preselected, form enabled)
 *        -> SUBMITTING (Add disabled + inline spinner)
 *        -> SUCCESS (dialogRef.close(result))  |  ERROR (toast, stay open)
 *
 * Notes:
 *  - Both User Account and Feature pickers are searchable comboboxes with
 *    type-to-filter, keyboard navigation (↓/↑/Enter/Escape), and click-outside-close.
 *  - Credit-voucher groups (Flow C) are filtered by the backend — not exposed via
 *    on load; callcenter-calls group is hidden implicitly because it has no
 *    addable features (AC-8a "no addable features" path).
 *  - UI strings are English inline literals — i18n is not yet implemented in
 *    AMIE (see CLAUDE.md §Internationalization). `translate()` retains a Map
 *    placeholder so a future i18n feature can wire in real translations.
 *
 * Reuses:
 *  - `CustomerBillingService` for getGroups / getFeatures / create.
 *  - `SessionService.userAccounts()` — UAs already loaded by customer detail shell.
 *  - `sign-normalizer.util` (BR-1) and `locale-decimal.util` (BR-2) helpers.
 */
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  HlmTextareaDirective,
  ToastService,
} from '../../../../ui';
import { SessionService } from '../../../../core/session/session.service';
import { LocalizationService } from '../../../../core/services/localization.service';
import { LoggingService } from '../../../../core/services/logging.service';
import { UserAccount } from '../../../../core/session/session.model';
import {
  BillingCreated,
  BillingGroup,
  BillingMessage,
  CustomerBillingService,
} from '../customer-billing.service';
import { CREDIT_GROUP, normalizeCreditUnitPrice } from './util/sign-normalizer.util';
import { parseLocaleDecimal } from './util/locale-decimal.util';
import {
  BILLING_GROUP_TO_CATEGORY_KEY,
  categoryLabelForGroup,
  isBookableFeature,
  isCallcenterGroup,
  isFeatureAvailable,
} from '../billing-categories';
import { FEATURE_LABELS_EN } from '../feature-labels';
import { buildBilingualLabel, EMPTY_LOCALIZATION_BAG } from '../bilingual-label.util';
import {
  featureOneLineLabel,
  featureSearchText,
  featureSecondLine,
  kvOrderIndex,
  resolveFeatureTexts,
} from '../feature-texts.util';
import { FeatureHelpComponent } from '../feature-label/feature-help.component';

/**
 * Localization API coordinates for the billing feature/category bag — the
 * legacy `featureShowname/defTarif` i18n namespace whose flat record contains
 * BOTH feature codes and group/category codes (EB-3886 / EB-3887).
 */
const FEATURE_SHOWNAME_GLOBAL_KEY = 'featureShowname';
const DEF_TARIF_CATEGORY = 'defTarif';
/** Localization API numeric language id for English. */
const ENGLISH_LANGUAGE_ID = '2';
/**
 * Localization API numeric language id for German: the **invoice text** of a
 * feature (tooltip, search, line-2 fallback, EB-4502) and the German parenthetical
 * of group labels. FIXED to German (EB-3887): brand/customer language is
 * deliberately NOT used. TODO: replace with the logged-in user's language once a
 * user-language signal is available.
 */
const SECONDARY_LANGUAGE_ID = '1';

/** Backend `name` code for the legacy index-1 "Nutzungsguthaben" group. */
const USAGE_CREDIT_GROUP = 'usageCreditGroup';

// Usage credit (Guthaben) needs no client-side group filter: /billings/groups does not return it (Flow C).

/** Input contract — resolved from `MAT_DIALOG_DATA`. */
export interface AddBillingDialogData {
  customerId: number;
  /**
   * Category currently active in the Billing Details grid filter.
   * If set and mappable to a concrete group, that group is pre-selected
   * on open (instead of the first non-credit group default).
   * Use `'ALL'` or `undefined` to skip pre-selection.
   */
  preselectedCategoryKey?: string;
}

/** Dialog lifecycle states driving the UI (fields enable/disable, spinner placement). */
export type DialogState = 'LOADING' | 'READY' | 'SUBMITTING';

@Component({
  selector: 'cmt-add-billing-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatAutocompleteModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmTextareaDirective,
    HlmSpinnerComponent,
    FeatureHelpComponent,
  ],
  templateUrl: './add-billing-dialog.component.html',
})
export class AddBillingDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AddBillingDialogComponent, BillingCreated[] | null>);
  readonly data: AddBillingDialogData = inject(MAT_DIALOG_DATA);

  private readonly fb = inject(FormBuilder);
  private readonly session = inject(SessionService);
  private readonly billingService = inject(CustomerBillingService);
  private readonly localizationService = inject(LocalizationService);
  private readonly toast = inject(ToastService);
  private readonly loggingService = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form: FormGroup = this.fb.group({
    /** Text bound to the Category <input> (mat-autocomplete). Not submitted. */
    groupSearch: this.fb.control<string | { id: number; name: string; label: string }>('', { nonNullable: true }),
    groupId: this.fb.control<number | null>(null, { validators: [Validators.required] }),
    /** Text bound to the Feature <input> (mat-autocomplete). Not submitted. */
    featureSearch: this.fb.control<string | { feature: string; label: string }>('', { nonNullable: true }),
    featureName: this.fb.control<string>('', { validators: [Validators.required] }),
    /** Text bound to the User Account <input> (mat-autocomplete). Not submitted. */
    userAccountSearch: this.fb.control<string | { id: number; firstName: string; lastName: string; fullName: string }>('', { nonNullable: true }),
    userAccountId: this.fb.control<number | null>(null, { validators: [Validators.required] }),
    quantityInput: this.fb.control<string>('', {
      validators: [Validators.required, this.validLocaleDecimal(), this.positiveQuantity()],
    }),
    unitPriceInput: this.fb.control<string>('', {
      validators: [Validators.required, this.validLocaleDecimal(), this.nonZeroCreditPrice()],
    }),
    description: this.fb.control<string>('', { validators: [Validators.maxLength(500)] }),
    /** Optional `YYYY-MM-DD` (date-only picker); empty means "stamp today" on submit (BR-3). */
    chargeOn: this.fb.control<string>(''),
    /**
     * Legacy "Nutzungsguthaben" Gültigkeitsdatum — shown instead of Quantity
     * for the Usage Credits group. `YYYY-MM-DD` (native date input format).
     */
    validUntil: this.fb.control<string>(''),
  });

  // ------- state signals -------
  readonly state = signal<DialogState>('LOADING');
  readonly groups = signal<BillingGroup[]>([]);
  /** Features the selected UA is allowed to book — drives the info-badge (AC-9). */
  readonly assignedFeatures = signal<string[] | null>(null);
  /**
   * Customer's default per-feature unit price (mirrors legacy
   * `KundeFeature.load(customerId, featureName).getEinheitenPreis()`).
   * Empty map = no defaults available (endpoint failed or feature not in plan).
   */
  private readonly featurePrices = signal<Map<string, number>>(new Map());

  /**
   * Localization bags for feature/category labels (EB-3886 / EB-3887). The
   * `featureShowname/defTarif` bag is a flat record keyed by BOTH feature codes
   * AND group codes. We fetch English (id 2) + the customer language once on
   * open and build "English (Deutsch)" labels from them. Empty maps (customer
   * unavailable or request failed) fall through to the static maps.
   */
  private readonly labelsEn = signal<ReadonlyMap<string, string>>(new Map());
  private readonly labelsCustomerLang = signal<ReadonlyMap<string, string>>(new Map());

  /**
   * Mirrors of key form values as signals so `computed()` members react when
   * the user edits the form. `toSignal` bridges the FormControl `valueChanges`
   * Observable into the signal graph — no manual `.subscribe` + `.set` wiring.
   */
  readonly groupIdSig = toSignal(this.form.controls.groupId.valueChanges, {
    initialValue: this.form.controls.groupId.value,
  }) as () => number | null;
  private readonly featureNameSig = toSignal(this.form.controls.featureName.valueChanges, {
    initialValue: this.form.controls.featureName.value ?? '',
  }) as () => string;
  private readonly userAccountIdSig = toSignal(this.form.controls.userAccountId.valueChanges, {
    initialValue: this.form.controls.userAccountId.value,
  }) as () => number | null;
  private readonly quantityInputSig = toSignal(this.form.controls.quantityInput.valueChanges, {
    initialValue: this.form.controls.quantityInput.value ?? '',
  }) as () => string;
  private readonly unitPriceInputSig = toSignal(this.form.controls.unitPriceInput.valueChanges, {
    initialValue: this.form.controls.unitPriceInput.value ?? '',
  }) as () => string;
  private readonly formStatusSig = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });
  private readonly formValiditySig = computed(() => this.formStatusSig() === 'VALID');

  /** User accounts are already in SessionService — no extra HTTP. */
  readonly userAccounts = computed(() => this.session.userAccounts());

  /**
   * Panel-open tracking for the three autocomplete dropdowns (EB-3801 #4
   * follow-up). The trailing icon is trash ONLY while the dropdown panel is
   * actively open — closed state always shows the chevron affordance,
   * regardless of whether a value is selected. Toggled by `(opened)/(closed)`
   * on each `mat-autocomplete`.
   */
  readonly groupPanelOpen = signal(false);
  readonly featurePanelOpen = signal(false);
  readonly uaPanelOpen = signal(false);

  readonly showGroupTrash = computed(() => this.groupPanelOpen());
  readonly showFeatureTrash = computed(() => this.featurePanelOpen());
  readonly showUaTrash = computed(() => this.uaPanelOpen());

  // ---- Searchable User Account autocomplete (AC-8) ----
  readonly uaFilter = signal('');

  /** Filtered UA list: matches firstName/lastName/fullName/login/id as substring. */
  readonly filteredUserAccounts = computed(() => {
    const q = this.uaFilter().trim().toLowerCase();
    const all = this.userAccounts();
    if (q.length === 0) return all;
    return all.filter((ua) => {
      const hay =
        `${ua.firstName} ${ua.lastName} ${ua.fullName} ${ua.login} ${ua.id}`.toLowerCase();
      return hay.includes(q);
    });
  });

  /** Currently-selected UA object — used to render the trigger label. */
  readonly selectedUserAccount = computed(() => {
    const id = this.userAccountIdSig();
    if (id == null) return null;
    return this.userAccounts().find((ua) => ua.id === id) ?? null;
  });

  // ---- Searchable Feature autocomplete ----
  readonly featureFilter = signal('');

  /**
   * Filtered features: substring match on every text of the feature — English,
   * KV text (corrected + original), invoice text and code (EB-3887 / EB-4502).
   */
  readonly filteredFeatures = computed(() => {
    const q = this.featureFilter().trim().toLowerCase();
    const all = this.featuresOfGroup();
    if (q.length === 0) return all;
    return all.filter((f) => f.search.includes(q));
  });

  /** Currently-selected feature option — used to render the trigger label. */
  readonly selectedFeatureOption = computed(() => {
    const name = this.featureNameSig();
    if (!name) return null;
    return this.featuresOfGroup().find((f) => f.feature === name) ?? null;
  });

  // ---- Searchable Category (Group) autocomplete ----
  readonly groupFilter = signal('');

  /** Resolved to the BillingGroup instance corresponding to the selected groupId. */
  readonly selectedGroup = computed<BillingGroup | null>(() => {
    const id = this.groupIdSig();
    if (id === null || id === undefined) return null;
    return this.groups().find((g) => g.id === id) ?? null;
  });

  /**
   * Legacy "Nutzungsguthaben" (Usage Credits) edge case — quantity is always 1
   * and the Quantity field is replaced by a "Valid until" date. Matches the
   * old KundenBillingFrame.java index-1 behaviour.
   */
  readonly isUsageCreditGroup = computed(
    () => this.selectedGroup()?.name === USAGE_CREDIT_GROUP,
  );

  /**
   * Groups with a resolved display label that matches the Billing Details
   * Category dropdown (so both dropdowns read identically to the user).
   * Label priority: mapped legacy-category label → backend description → `name`.
   */
  /**
   * Filtered groups for the combobox — matches the bilingual label (which
   * embeds both the English AND customer-language strings) plus the raw group
   * `name` code as substring (EB-3886).
   */
  readonly filteredGroupOptions = computed(() => {
    const q = this.groupFilter().trim().toLowerCase();
    const all = this.groupOptions();
    if (q.length === 0) return all;
    return all.filter((g) => `${g.label} ${g.name}`.toLowerCase().includes(q));
  });

  /** Label of the currently selected group — used for the combobox trigger. */
  readonly selectedGroupLabel = computed(() => {
    const id = this.groupIdSig();
    if (id == null) return null;
    return this.groupOptions().find((g) => g.id === id)?.label ?? null;
  });

  readonly groupOptions = computed(() => {
    const assigned = this.assignedFeatures();
    // Null = fetch pending or failed → show all groups (safe default).
    // Array = show only groups that have at least one feature the selected
    // UA is allowed to book (mirrors legacy kunde.hasFeature gating).
    const assignedSet = assigned ? new Set(assigned) : null;
    // Categories take their English from the curated categoryLabelForGroup (the
    // i18n bag has no English group names), so only the customer-language bag is
    // needed here for the parenthetical.
    const bagLocal = this.labelsCustomerLang();
    return this.groups()
      .filter((g) => {
        if (!assignedSet) return true;
        return (g.features ?? []).some((f) => isFeatureAvailable(f.feature, assignedSet));
      })
      .map((g) => ({
        id: g.id,
        // Raw backend code — kept on the option so the type-ahead can match it.
        name: g.name,
        // Bilingual "English (Deutsch)" label (EB-3886/3887). Groups have NO
        // English entry in the i18n bag — it returns the German group name for
        // every language. So pass an EMPTY English bag to force the curated
        // English `categoryLabelForGroup` as primary, and keep the (German)
        // customer-language bag for the parenthetical.
        label: buildBilingualLabel(
          g.name,
          EMPTY_LOCALIZATION_BAG,
          bagLocal,
          categoryLabelForGroup(g.name, g.description ?? g.name),
        ),
      }));
  });

  /**
   * Features within the selected group with all their texts (EB-4502): English
   * (line 1), KV text (line 2), invoice text (tooltip). `label` is the
   * single-line `English (KV text)` form shown in the input once selected.
   *
   * Order follows the KV dropdown of the group (EB-4502 step 4); codes KV does
   * not offer come last, alphabetically by English text.
   *
   * English text: `ENGLISH_OVERRIDES` (feature-texts.util.ts) wins over the bag;
   * English-bag-miss fallback chain:
   *   1. static English map FEATURE_LABELS_EN (legacy KundenBilling labels)
   *   2. backend `showName` (from `/billings/groups`)
   *   3. raw feature code as last resort
   *
   * The "ignore showName when it just echoes the code" guard is retained — the
   * live API often returns the code back, which would defeat downstream lookups.
   */
  readonly featuresOfGroup = computed(() => {
    const assigned = this.assignedFeatures();
    const assignedSet = assigned ? new Set(assigned) : null;
    const bagEn = this.labelsEn();
    const bagLocal = this.labelsCustomerLang();
    const group = this.selectedGroup();
    const categoryKey = group ? (BILLING_GROUP_TO_CATEGORY_KEY[group.name] ?? null) : null;
    return (group?.features ?? [])
      .filter((f) => isFeatureAvailable(f.feature, assignedSet))
      .map((f) => {
        const backend =
          f.showName && f.showName.trim().length > 0 && f.showName !== f.feature
            ? f.showName
            : null;
        const fallback = FEATURE_LABELS_EN[f.feature] ?? backend ?? f.feature;
        const texts = resolveFeatureTexts(f.feature, categoryKey, bagEn, bagLocal, fallback);
        return {
          feature: f.feature,
          label: featureOneLineLabel(texts),
          secondLine: featureSecondLine(texts),
          search: featureSearchText(texts),
          order: kvOrderIndex(f.feature, categoryKey),
          texts,
        };
      })
      .sort((a, b) => a.order - b.order || a.texts.english.localeCompare(b.texts.english));
  });

  /** Empty-state flags — each one disables the Add button (AC-8a). */
  readonly noGroups = computed(() => this.state() !== 'LOADING' && this.groups().length === 0);
  readonly noUserAccounts = computed(
    () => this.state() !== 'LOADING' && this.userAccounts().length === 0,
  );
  /**
   * The selected UA has no billable categories — fires once the assigned-features
   * fetch finishes and `groupOptions` filters down to zero. Surfaces a clear
   * inline alert so the user immediately knows to pick a different UA.
   */
  readonly noCategoriesForUa = computed(() => {
    if (this.state() !== 'READY') return false;
    if (this.assignedFeatures() === null) return false;
    if (this.userAccountIdSig() == null) return false;
    return this.groupOptions().length === 0;
  });
  readonly selectedGroupHasNoFeatures = computed(() => {
    const g = this.selectedGroup();
    return g !== null && g.features.length === 0;
  });

  /**
   * Info-badge visibility — shown when the current UA does NOT own the selected
   * feature. Silently suppressed on `assignedFeatures === null` (either never
   * fetched or last fetch failed — AC-9 rules).
   */
  readonly showFeatureMismatchBadge = computed(() => {
    const assigned = this.assignedFeatures();
    const feature = this.featureNameSig();
    if (assigned === null) return false;
    if (!feature) return false;
    return !isFeatureAvailable(feature, new Set(assigned));
  });

  /** Convenience flag for the submit button. */
  readonly submitting = computed(() => this.state() === 'SUBMITTING');

  /**
   * "Customer 13279 · Schlau, Markus" meta rendered in the dialog header,
   * matching the mockup. Empty string if session has no customer (standalone use).
   */
  readonly customerMeta = computed(() => {
    const c = this.session.customer();
    if (!c) return '';
    // Prefer company name for corporate customers (Schlaue Software GmbH,
    // ebuero AG, …); fall back to the contract's full name, then first/last,
    // then bare id. A first/last-only display would return "Customer 13279"
    // alone for corporate rows — which the mockup's `Customer 13279 · {name}`
    // meta tries to avoid.
    const name =
      c.companyName?.trim() ||
      c.contractFullName?.trim() ||
      [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
    return name ? `Customer ${c.id} · ${name}` : `Customer ${c.id}`;
  });

  /**
   * Live total preview shown bottom-left of the footer (mockup parity).
   * Formatted in German locale ("€ 3,75"). Returns empty string when either
   * input isn't a valid decimal — the footer collapses to show buttons only.
   * Uses BR-1 sign normalization (sign on the unit price, quantity stays
   * positive) so credit-group rows show a negative total.
   */
  readonly livePreviewTotal = computed(() => {
    const qty = parseLocaleDecimal(this.quantityInputSig());
    const price = parseLocaleDecimal(this.unitPriceInputSig());
    if (!Number.isFinite(qty) || !Number.isFinite(price)) return '';
    const groupName = this.selectedGroup()?.name ?? '';
    const signedPrice = normalizeCreditUnitPrice(groupName, price);
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(qty * signedPrice);
  });

  /**
   * Add button disabled only for states the user cannot resolve by editing
   * the form: loading, submitting, or no groups/UAs/features to pick from.
   * An invalid form keeps the button ENABLED so clicking Add surfaces
   * inline field errors via `markAllAsTouched()` in `submit()`.
   */
  readonly addDisabled = computed(() => {
    if (this.state() === 'LOADING' || this.state() === 'SUBMITTING') return true;
    if (this.noGroups() || this.noUserAccounts() || this.selectedGroupHasNoFeatures()) return true;
    return false;
  });

  // ------- lifecycle -------
  ngOnInit(): void {
    // Fields stay disabled until READY.
    this.form.disable({ emitEvent: false });

    // Side-effect handlers — mirror signals are wired via `toSignal` on the
    // control declarations above. These subscriptions only do side-effect work
    // (reset dependent fields, fetch data) that can't be expressed as pure
    // derivations.
    this.form.controls.groupId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Keep the Category <input> text in sync with the committed id so
        // that the label shows the group name after programmatic setValue.
        const opt = this.groupOptions().find((g) => g.id === this.selectedGroup()?.id);
        if (opt) this.form.controls.groupSearch.setValue(opt, { emitEvent: false });

        // Auto-select the first feature of the new group (or clear if empty)
        // so the user doesn't have to manually open the Feature dropdown.
        // Uses `featuresOfGroup()` (already filtered by the current UA's
        // assigned features) so we never pre-select an unavailable feature.
        const firstFeature = this.featuresOfGroup()[0]?.feature ?? '';
        this.form.controls.featureName.setValue(firstFeature);
        // Disable the feature control when the selected group has no addable features.
        const hasFeatures = (this.selectedGroup()?.features.length ?? 0) > 0;
        if (hasFeatures) {
          this.form.controls.featureName.enable({ emitEvent: false });
        } else {
          this.form.controls.featureName.disable({ emitEvent: false });
        }
        // Usage-credit groups swap Quantity → Valid-until (legacy Nutzungsguthaben).
        this.syncUsageCreditValidators();
        // The zero-price block only applies to credit groups — re-evaluate the
        // price when the group changes while a value is already typed.
        this.form.controls.unitPriceInput.updateValueAndValidity();
      });
    this.form.controls.featureName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        const name = (v as string) ?? '';
        if (name) {
          // Commit: sync the Feature <input> text with the committed feature.
          // Only set when we have a matching option — an empty/unknown name
          // must NOT clobber user-typed text in the autocomplete input.
          const opt = this.featuresOfGroup().find((f) => f.feature === name);
          if (opt) this.form.controls.featureSearch.setValue(opt, { emitEvent: false });
        }
        this.applyFeatureDefaultPrice(name);
      });
    this.form.controls.userAccountId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((uaId) => {
        if (typeof uaId === 'number') {
          // Commit: sync the UA <input> text with the selected account.
          const ua = this.userAccounts().find((u) => u.id === uaId);
          if (ua) this.form.controls.userAccountSearch.setValue(ua, { emitEvent: false });
          this.fetchAssignedFeatures(uaId);
        }
      });

    this.loadInitialData();
  }

  // ------- public actions -------

  /**
   * Swap Quantity ↔ Valid-until validators when the selected group toggles
   * usage-credit mode. In usage-credit mode Quantity is hard-coded to 1 on
   * submit, so the field doesn't need user input — the date becomes required
   * instead.
   */
  private syncUsageCreditValidators(): void {
    const quantity = this.form.controls.quantityInput;
    const validUntil = this.form.controls.validUntil;
    if (this.isUsageCreditGroup()) {
      quantity.clearValidators();
      quantity.setValue('1');
      validUntil.setValidators([Validators.required]);
    } else {
      quantity.setValidators([Validators.required, this.validLocaleDecimal(), this.positiveQuantity()]);
      validUntil.clearValidators();
      validUntil.setValue('', { emitEvent: false });
    }
    quantity.updateValueAndValidity({ emitEvent: false });
    validUntil.updateValueAndValidity({ emitEvent: false });
  }

  /** Validator: checks input parses to a finite number via locale-aware decimal parser. */
  private validLocaleDecimal(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '') as string;
      if (value.trim().length === 0) return null; // let Validators.required own emptiness
      const parsed = parseLocaleDecimal(value);
      return Number.isNaN(parsed) ? { localeDecimal: true } : null;
    };
  }

  /**
   * Validator: quantity must be a positive count. The backend silently stores
   * any quantity below 0 as 0 (invoice-api `setUpBillingDBO`), so a non-positive
   * quantity would create a 0 € line (EB-4521). Unparseable input is left to
   * {@link validLocaleDecimal}.
   */
  private positiveQuantity(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '') as string;
      if (value.trim().length === 0) return null;
      const parsed = parseLocaleDecimal(value);
      if (Number.isNaN(parsed)) return null;
      return parsed > 0 ? null : { quantityNotPositive: true };
    };
  }

  /**
   * Validator: a credit (Gutschrift) must carry a price other than 0 — a 0 €
   * credit is meaningless (EB-4521 / EB-4140, agreed with the BE dev). Legacy KV
   * accepts 0; this is a deliberate deviation. The sign itself is not validated
   * because it is normalized to negative on submit.
   */
  private nonZeroCreditPrice(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // `form` is built before the `selectedGroup` computed exists, and Angular
      // runs validators once at control construction — hence the optional call.
      if (this.selectedGroup?.()?.name !== CREDIT_GROUP) return null;
      const value = (control.value ?? '') as string;
      if (value.trim().length === 0) return null;
      const parsed = parseLocaleDecimal(value);
      return parsed === 0 ? { creditPriceZero: true } : null;
    };
  }

  /**
   * Submit handler — BR-1 sign normalization (credit sign on the unit price,
   * quantity always positive) + BR-2 locale-aware decimals +
   * BR-3 default chargeOn stamping. Keeps the dialog open on failure with
   * user-entered values preserved.
   */
  submit(): void {
    if (this.state() === 'SUBMITTING' || this.state() === 'LOADING') return;
    if (this.noGroups() || this.noUserAccounts() || this.selectedGroupHasNoFeatures()) return;
    if (this.form.invalid) {
      // Surface inline error messages on every untouched field so the user
      // knows what's missing (e.g. pressing Add without focusing Category
      // would otherwise silently do nothing because the Add button guard
      // was already blocking the submit).
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const group = this.selectedGroup();
    if (!group) return;

    // Credit sign lives on the unit price (legacy KV); the backend zeroes a
    // negative quantity, so `amount` is always the positive typed quantity.
    const unitPrice = normalizeCreditUnitPrice(group.name, parseLocaleDecimal(raw.unitPriceInput ?? ''));
    // Legacy Nutzungsguthaben: quantity is always 1; the Valid-until date
    // rides along in additionalInfo since /billings has no dedicated field.
    const usageCreditMode = this.isUsageCreditGroup();
    const amount = usageCreditMode ? 1 : parseLocaleDecimal(raw.quantityInput ?? '');
    // Backend `chargeOn` is a Java LocalDateTime — must be ISO *local* with
    // seconds precision, WITHOUT a timezone suffix and WITHOUT fractional
    // seconds. Sending `new Date().toISOString()` (which appends 'Z') is a 400.
    // Picker is date-only; combine with current wall-clock time so same-day
    // rows keep insertion order on the server.
    const chargeOnLocal = raw.chargeOn
      ? this.toLocalDateTimeSeconds(this.combinePickedDateWithNow(raw.chargeOn))
      : this.toLocalDateTimeSeconds(new Date());
    const typedDescription = (raw.description ?? '').trim();
    const validUntilPrefix =
      usageCreditMode && raw.validUntil
        ? `Valid until: ${raw.validUntil}`
        : '';
    const description = [validUntilPrefix, typedDescription]
      .filter((s) => s.length > 0)
      .join(' — ');

    // These are guaranteed non-null at submit time by form `required` validators —
    // the form.invalid short-circuit above is the enforcement.
    const featureName = raw.featureName as string;
    const userAccountId = raw.userAccountId as number;
    const message: BillingMessage = {
      featureName,
      userAccountId,
      customerId: String(this.data.customerId),
      chargeOn: chargeOnLocal,
      unitPrice,
      amount,
      ...(description.length > 0 ? { additionalInfo: description } : {}),
    };

    this.state.set('SUBMITTING');
    this.billingService.create(message).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        /** @ticket EB-3798 — usage: a billing item was successfully created (D9). */
        this.loggingService.usage('action.invoke', { action: 'add_billing', outcome: 'success' });
        this.dialogRef.close(result);
      },
      error: (err: { error?: { message?: string } }) => {
        const msg =
          err?.error?.message ??
          this.translate('billingItems.toast.error', 'Could not save billing. Please try again.');
        this.toast.open(msg, 'OK', { duration: 5000, tone: 'error' });
        this.state.set('READY');
      },
    });
  }

  /** Cancel closes with null so the caller's `afterClosed()` sees a falsy result. */
  cancel(): void {
    this.dialogRef.close(null);
  }

  // ---- User Account autocomplete ----

  displayUserAccount = (ua: UserAccount | string): string => {
    if (!ua) return '';
    if (typeof ua === 'string') return ua;
    const name = ua.fullName || `${ua.firstName} ${ua.lastName}`.trim();
    return `${name} (${ua.id})`;
  };

  onUaSearch(): void {
    const val = this.form.controls.userAccountSearch.value;
    const typed = typeof val === 'string' ? val : '';
    this.uaFilter.set(typed);
    if (typeof val === 'string') {
      this.form.controls.userAccountId.setValue(null);
    }
  }

  onUaSelected(event: MatAutocompleteSelectedEvent): void {
    const ua = event.option.value as UserAccount;
    this.form.controls.userAccountId.setValue(ua.id);
    this.form.controls.userAccountSearch.setValue(ua);
    this.uaFilter.set('');
  }

  clearUa(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.form.controls.userAccountSearch.setValue('');
    this.form.controls.userAccountId.setValue(null);
    this.uaFilter.set('');
    // Close the panel + revert icon to chevron immediately.
    this.uaPanelOpen.set(false);
    (event.currentTarget as HTMLElement | null)?.blur();
  }

  /**
   * Select the entire input contents on focus so the user can start typing
   * a new search immediately without manually clearing the previous value.
   * Used by Category, Feature and User Account autocomplete inputs.
   */
  selectInputContents(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    // Defer so the browser's normal focus handling completes first; otherwise
    // the selection is occasionally collapsed back to a caret on click.
    setTimeout(() => input.select(), 0);
  }

  // ---- Category (Group) autocomplete ----

  /** `displayWith` for mat-autocomplete — label for selected option. */
  displayGroup = (option: { id: number; name: string; label: string } | string): string => {
    if (!option) return '';
    return typeof option === 'string' ? option : option.label;
  };

  /** User typing in the Category input — clear committed selection if free text. */
  onGroupSearch(): void {
    const val = this.form.controls.groupSearch.value;
    const typed = typeof val === 'string' ? val : '';
    this.groupFilter.set(typed);
    // Only reset the committed groupId if user is typing freely (not clicking
    // an option — that fires (optionSelected) which re-commits).
    if (typeof val === 'string') {
      this.form.controls.groupId.setValue(null);
    }
  }

  /** Committed selection from the Category dropdown. */
  onGroupSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as { id: number; name: string; label: string };
    this.form.controls.groupId.setValue(option.id);
    this.form.controls.groupSearch.setValue(option);
    this.groupFilter.set('');
  }

  clearGroup(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.form.controls.groupSearch.setValue('');
    this.form.controls.groupId.setValue(null);
    this.groupFilter.set('');
    this.groupPanelOpen.set(false);
    (event.currentTarget as HTMLElement | null)?.blur();
  }

  // ---- Feature autocomplete ----

  displayFeature = (option: { feature: string; label: string } | string): string => {
    if (!option) return '';
    return typeof option === 'string' ? option : option.label;
  };

  onFeatureSearch(): void {
    const val = this.form.controls.featureSearch.value;
    const typed = typeof val === 'string' ? val : '';
    this.featureFilter.set(typed);
    if (typeof val === 'string') {
      this.form.controls.featureName.setValue('');
    }
  }

  onFeatureSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as { feature: string; label: string };
    this.form.controls.featureName.setValue(option.feature);
    this.form.controls.featureSearch.setValue(option);
    this.featureFilter.set('');
  }

  clearFeature(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.form.controls.featureSearch.setValue('');
    this.form.controls.featureName.setValue('');
    this.featureFilter.set('');
    this.featurePanelOpen.set(false);
    (event.currentTarget as HTMLElement | null)?.blur();
  }

  /**
   * Resolve a translation with a safe English fallback. i18n is not yet
   * implemented in AMIE (see CLAUDE.md §Internationalization) — currently
   * always returns the fallback. A future i18n feature will wire in real
   * translations here.
   */
  translate(_key: string, fallback: string): string {
    return fallback;
  }

  // ------- internals -------

  private loadInitialData(): void {
    const customerId = this.data.customerId;

    // Fetch the bilingual label bags in parallel with groups/prices (EB-3886 /
    // EB-3887). Independent of the groups → prices chain below, so kicked off
    // separately. Failures / missing customer fall back to the static maps.
    this.loadLabelBags();

    // Prices depend on groups (we need every feature name to pass to
    // `/contract/v2/rest/features/booked`), so chain via switchMap once groups
    // have arrived. Feature/category labels come primarily from the i18n bags
    // loaded above; they fall back through FEATURE_LABELS_EN /
    // categoryLabelForGroup → backend `showName` → raw code (see
    // `featuresOfGroup` / `groupOptions`).
    this.billingService.getGroups()
      .pipe(
        // Drop non-bookable catalog features (e.g. gut_start, EB-4502) at the
        // source so options, type-ahead, assignment checks and the price
        // lookup never see them.
        map((groups) =>
          groups.map((g) => ({ ...g, features: g.features.filter((f) => isBookableFeature(f.feature)) })),
        ),
        switchMap((groups) => {
          const featureNames = Array.from(
            new Set(groups.flatMap((g) => g.features.map((f) => f.feature))),
          );
          return this.billingService
            .getDefaultFeaturePrices(customerId, featureNames)
            .pipe(switchMap((prices) => of({ groups, prices })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ groups, prices }) => {
          const visible = groups
            .slice()
            // Callcenter Calls is excluded from Add Billing — callcenter usage
            // is generated automatically by the telephony pipeline.
            .filter((g) => !isCallcenterGroup(g.name, g.description))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          this.groups.set(visible);
          this.featurePrices.set(prices);
          this.applyReadyState();
        },
        error: () => {
          // Dialog is effectively unusable without groups/prices — empty-state
          // guards keep Add disabled, and this toast tells the user why
          // instead of leaving them guessing.
          this.groups.set([]);
          this.featurePrices.set(new Map());
          this.toast.open(
            this.translate(
              'billingItems.toast.loadError',
              'Failed to load billing configuration. Please close and try again.',
            ),
            'OK',
            { duration: 5000, tone: 'error' },
          );
          this.applyReadyState();
        },
      });
  }

  /**
   * Load the English + customer-language i18n label bags once on open
   * (EB-3886 / EB-3887). Both come from the legacy `featureShowname/defTarif`
   * namespace whose flat record is keyed by BOTH feature codes AND group codes.
   *
   * `LocalizationService.getTranslations` already caches per
   * (brand, language, globalKey, category) and swallows HTTP errors into an
   * empty map — so a failed request or a customer with an unusual language id
   * just leaves the bag empty and the labels fall back to the static maps.
   *
   * When the customer is unavailable (standalone use) we skip the fetch
   * entirely and keep both bags empty (static-map fallback).
   */
  private loadLabelBags(): void {
    const customer = this.session.customer();
    // Skip when the customer (or its brand) is unavailable — labels then fall
    // back to the static maps.
    if (!customer || typeof customer.brandId !== 'number') return;

    forkJoin({
      en: this.localizationService.getTranslations(
        customer.brandId,
        ENGLISH_LANGUAGE_ID,
        FEATURE_SHOWNAME_GLOBAL_KEY,
        DEF_TARIF_CATEGORY,
      ),
      // Parenthetical fixed to German (brand/customer language deliberately not
      // used — see SECONDARY_LANGUAGE_ID). Brand stays the customer's brand so
      // the German names match that brand's catalog.
      local: this.localizationService.getTranslations(
        customer.brandId,
        SECONDARY_LANGUAGE_ID,
        FEATURE_SHOWNAME_GLOBAL_KEY,
        DEF_TARIF_CATEGORY,
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ en, local }) => {
          this.labelsEn.set(en);
          this.labelsCustomerLang.set(local);
        },
        // getTranslations already maps errors to empty maps; this guard only
        // catches a forkJoin-level failure. Either way we keep the static maps.
        error: () => void 0,
      });
  }

  /**
   * Populate the Unit Price field from the customer's per-feature price map,
   * mirroring legacy `KundeFeature.load(..).getEinheitenPreis()`. Called on
   * feature change AND during `applyReadyState` so the default feature also
   * gets its price.
   *
   * Special case for the credit group: legacy KV opened credit features with
   * unit price `0` (`FEATURE_GUTSCHRIFT_KUENDIGUNG`) and the operator typed the
   * amount. A credit price of 0 is now invalid (EB-4521, `nonZeroCreditPrice`),
   * so credits open with an EMPTY price: `Validators.required` prompts the
   * operator instead of the form opening invalid by design.
   *
   * Missing lookup leaves the field untouched — the user sees their last
   * entry, not a surprise overwrite to 0.
   */
  private applyFeatureDefaultPrice(featureName: string): void {
    if (!featureName) return;
    const group = this.selectedGroup();
    if (group?.name === CREDIT_GROUP) {
      this.form.controls.unitPriceInput.setValue('');
      return;
    }
    const price = this.featurePrices().get(featureName);
    if (price === undefined) return; // no data → leave user's existing input alone
    // Format canonically (dot form). Tests + backend tolerate both separators.
    this.form.controls.unitPriceInput.setValue(String(price));
  }

  /**
   * Transition to READY. Applies legacy-parity defaults (mirrors the Swing
   * `Hinzufügen` form in KundenBillingFrame): first Group, first Feature of
   * that Group, first UA, Quantity=1, Unit Price=0, Billing Date=now. User
   * can override any field before submitting.
   */
  private applyReadyState(): void {
    this.state.set('READY');
    this.form.enable({ emitEvent: false });

    // Default Group = first non-credit group (so we don't open the dialog with a
    // sign-forced-negative group preselected — matches the legacy Swing screen
    // which defaulted to Sekretariatsdienst rather than Gutschrift).
    // Falls back to the very first group if every group is a credit group.
    const defaults = this.groups();
    // Pre-select group matching the grid filter category, if provided and
    // mappable to one of our loaded groups. Otherwise fall back to the
    // first non-credit group (matches legacy Swing screen default).
    const preselectedKey = this.data.preselectedCategoryKey;
    const groupFromCategory =
      preselectedKey && preselectedKey !== 'ALL'
        ? defaults.find(
            (g) => BILLING_GROUP_TO_CATEGORY_KEY[g.name] === preselectedKey,
          )
        : undefined;
    const firstGroup =
      groupFromCategory ??
      defaults.find((g) => g.name !== CREDIT_GROUP) ??
      defaults[0];
    if (firstGroup) {
      // valueChanges handler auto-selects the first feature of the new group,
      // so we don't need a follow-up `featureName.setValue(...)` here.
      this.form.controls.groupId.setValue(firstGroup.id);
    }

    // Default UA = the "Zentrale" / main account of the customer.
    // Heuristic (no explicit "main" flag in the UA payload): the first
    // ACTIVE user account by ascending ID. `status === 'aktiv'` filters
    // out deactivated legacy UAs (which the sidebar's lastName sort
    // incorrectly surfaces first — see customer 78159 where
    // "Assistenz der Geschäftsleitung" (deaktiv) gets auto-selected).
    // Oldest active ID is typically the "Zentrale" main line.
    const uas = this.userAccounts();
    const activeUas = uas.filter((ua) => ua.status === 'aktiv');
    const sortedActive = [...activeUas].sort((a, b) => a.id - b.id);
    const defaultUaId = sortedActive[0]?.id ?? uas[0]?.id ?? null;
    if (defaultUaId != null) {
      // Setting triggers valueChanges => fetchAssignedFeatures.
      this.form.controls.userAccountId.setValue(defaultUaId);
    }

    // Defaults matching the legacy Swing screen: Menge=1, Datum=today.
    // Raw '1' parses cleanly in both locales.
    // Unit Price is NOT hard-coded here — `applyFeatureDefaultPrice` already
    // ran via the featureName valueChanges handler when the default feature
    // was set above, populating the user's per-feature price (or leaving a
    // credit-group feature empty for the operator to fill in).
    this.form.controls.quantityInput.setValue('1');
    this.form.controls.chargeOn.setValue(this.todayAsDateLocal());
    // Fallback: if featurePrices was empty (endpoint failed / no plan) and
    // the field is still blank, seed it to '0' to keep the form valid.
    // Credits stay empty: 0 is not a valid credit price (EB-4521).
    if (
      (this.form.controls.unitPriceInput.value ?? '') === '' &&
      this.selectedGroup()?.name !== CREDIT_GROUP
    ) {
      this.form.controls.unitPriceInput.setValue('0');
    }
  }

  /**
   * Formats a Date as `YYYY-MM-DDTHH:mm:ss` in LOCAL time — the wire format
   * the backend's Java LocalDateTime deserializer accepts.
   * Verified against `/invoice/v1/billings` POST 2026-04-21: fractional
   * seconds ('.000') and `Z` suffix both trigger 400.
   */
  private toLocalDateTimeSeconds(d: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    );
  }

  /** `YYYY-MM-DD` in LOCAL tz for `<input type="date">` seeding. */
  private todayAsDateLocal(): string {
    const d = new Date();
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /**
   * Combines a `YYYY-MM-DD` from the date picker with the current wall-clock
   * time. Parsing `new Date("YYYY-MM-DD")` would interpret the string as UTC
   * midnight and shift by the local offset, so we split manually.
   */
  private combinePickedDateWithNow(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map((s) => Number(s));
    const now = new Date();
    return new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds());
  }

  /**
   * Fetch the feature names assigned to the given UA. Drives both the
   * category/feature dropdown filtering AND the mismatch reset below.
   * Failures leave `assignedFeatures` at `null` → no filtering applied
   * (safe fallback: the backend will reject the final POST).
   */
  private fetchAssignedFeatures(userAccountId: number): void {
    this.assignedFeatures.set(null);
    this.billingService.getFeatures(userAccountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (features) => {
          this.assignedFeatures.set(features);
          this.reconcileSelectionAgainstAssignedFeatures(features);
        },
        error: () => this.assignedFeatures.set(null),
      });
  }

  /**
   * After the assigned-features list arrives for a new UA, CLEAR any
   * Category/Feature selection that is no longer available and surface
   * validation (markAsTouched) so the user sees the red border + required
   * error. We intentionally do NOT auto-pick a replacement — the user
   * must consciously choose what to book for the new UA.
   */
  private reconcileSelectionAgainstAssignedFeatures(assigned: string[]): void {
    const assignedSet = new Set(assigned);
    const currentFeature = this.form.controls.featureName.value as string;
    const currentGroup = this.selectedGroup();
    const currentGroupHasFeature =
      currentGroup?.features.some((f) => assignedSet.has(f.feature)) ?? false;

    // Only clear the selection when the group has features but NONE of them
    // are assigned to the UA. An empty group (features.length === 0) is its
    // own state — handled by `selectedGroupHasNoFeatures` and surfaced as a
    // disabled Feature dropdown — and must remain selected so the user
    // continues to see which category bucket is current.
    if (currentGroup && currentGroup.features.length > 0 && !currentGroupHasFeature) {
      this.form.controls.groupId.setValue(null);
      this.form.controls.groupSearch.setValue('');
      this.form.controls.featureName.setValue('');
      this.form.controls.featureSearch.setValue('');
      this.form.controls.groupId.markAsTouched();
      this.form.controls.featureName.markAsTouched();
      return;
    }

    if (currentFeature && !assignedSet.has(currentFeature)) {
      // Group is valid but the picked feature isn't — clear feature only.
      this.form.controls.featureName.setValue('');
      this.form.controls.featureSearch.setValue('');
      this.form.controls.featureName.markAsTouched();
    }
  }
}
